/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api\httpServerApi.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    API calls for the HTTP/HTTPS Server configuration module.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { HttpServerConfig, HttpServerFormState } from '../types';

/** Slim certificate descriptor returned by getCertificates() */
export interface CertificateOption {
    id: number;
    name: string;
    commonName: string;
    status: string;
}

export interface HttpServerApiResponse {
    success: boolean;
    data: HttpServerConfig;
    message?: string;
}

/**
 * Get HTTP Server configuration
 */
export async function getHttpServerConfig(
    tenantSlug?: string,
    userId?: string
): Promise<HttpServerApiResponse> {
    const response = await fetch(buildApiUrl('/api/maintenance/http-server'), {
        headers: getApiHeaders(tenantSlug, userId)
    });

    if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
    }

    return await response.json();
}

/**
 * Update HTTP Server configuration
 */
export async function updateHttpServerConfig(
    data: Partial<HttpServerFormState>,
    tenantSlug?: string,
    userId?: string
): Promise<HttpServerApiResponse> {
    const response = await fetch(buildApiUrl('/api/maintenance/http-server'), {
        method: 'PUT',
        headers: {
            ...getApiHeaders(tenantSlug, userId),
            'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP ${response.status}`);
    }

    return await response.json();
}

/**
 * Reset HTTP Server configuration to defaults
 */
export async function resetHttpServerConfig(
    tenantSlug?: string,
    userId?: string
): Promise<HttpServerApiResponse> {
    const response = await fetch(buildApiUrl('/api/maintenance/http-server/reset'), {
        method: 'POST',
        headers: {
            ...getApiHeaders(tenantSlug, userId),
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
    });

    if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || `HTTP ${response.status}`);
    }

    return await response.json();
}

/**
 * Get available certificates for the domain certificate dropdown (ACTIVE only)
 */
export async function getCertificates(
    tenantSlug?: string,
    userId?: string
): Promise<CertificateOption[]> {
    const response = await fetch(buildApiUrl('/api/maintenance/certificates'), {
        headers: getApiHeaders(tenantSlug, userId)
    });

    if (!response.ok) return [];

    const json = await response.json();
    // API returns { success, data: [...] } or bare array
    const list: any[] = json?.data ?? json ?? [];
    return list.map((c) => ({
        id: c.id,
        name: c.name,
        commonName: c.commonName ?? c.common_name ?? '',
        status: c.status ?? 'ACTIVE'
    }));
}
